import type { LocationType } from "api-types/builders/builderTypes";
import { getTopLocationLevel, getLocationTypeLabel } from "api-types/locations/locationDisplay";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { formatDollarsWholeNumber } from "common/utils/formatDollars";
import pluralize from "common/utils/pluralize";
import { useUserContext } from "lib/state/UserContextProvider";
import type { TreeSelectNodeType } from "ui/components/TreeSelect";
import { ReadOnlyTreeSelect } from "ui/components/TreeSelect";
import cx from "ui/cx";
import { Tooltip } from "ui/popovers/Tooltip";
import styles from "./MasterPlanReviewModal.module.scss";

export interface MasterPlanSummaryPlanItem {
  id: string;
  name?: string | null;
  basePrice?: number | null;
}

interface MasterPlanReviewSummaryProps {
  plans: Array<MasterPlanSummaryPlanItem | ClientMasterPlan>;
  treeData: TreeSelectNodeType[];
  selectedLocationIds: string[];
  /** Map of masterPlanId -> locationIds already containing that plan */
  duplicates?: Record<string, string[]>;
  /** Use "X new location(s)" header format instead of "Locations (X)" */
  useNewLocationsLabel?: boolean;
  /** Optional tooltip builder for duplicates, defaults to count */
  getDuplicateTooltip?: (planId: string) => string;
  /** Optional: reduce top spacing to fit tighter in bulk review */
  compactTopSpacing?: boolean;
}

export default function MasterPlanReviewSummary({
  plans,
  treeData,
  selectedLocationIds,
  duplicates,
  useNewLocationsLabel,
  getDuplicateTooltip,
  compactTopSpacing,
}: MasterPlanReviewSummaryProps) {
  const { builder } = useUserContext();
  const getDuplicateCount = (planId: string): number => {
    return duplicates?.[planId]?.length ?? 0;
  };

  const topLocationLevel = getTopLocationLevel(builder?.locationSettings);

  return (
    <Tooltip.Provider>
      <div className={cx(styles.wrapper, compactTopSpacing && styles.summaryWrapper)}>
        {plans.length > 0 && (
          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Master Plans ({plans.length})</h4>
            <div className={styles.plansList}>
              {plans.map((plan) => {
                const id = (plan as MasterPlanSummaryPlanItem).id;
                const duplicateCount = getDuplicateCount(id);
                const tooltip = getDuplicateTooltip?.(id);
                return (
                  <div key={id} className={styles.planItem}>
                    <div className={styles.planName}>{plan.name}</div>
                    <div className={styles.planPrice}>{formatDollarsWholeNumber(plan.basePrice ?? 0)}</div>
                    {duplicateCount > 0 && (
                      <Tooltip.Root>
                        <Tooltip.Trigger>
                          <div className={styles.duplicateWarning}>
                            {duplicateCount} duplicate{duplicateCount > 1 ? "s" : ""}
                          </div>
                        </Tooltip.Trigger>
                        <Tooltip.Content side="top" className={styles.duplicateTooltip}>
                          {tooltip ? (
                            <>
                              <div className={styles.duplicateTooltipTitle}>
                                Plan already exists on the following locations:
                              </div>
                              <div className={styles.duplicateTooltipList}>{tooltip}</div>
                              <div className={styles.duplicateTooltipNote}>
                                This action creates duplicate plans for each location.
                              </div>
                            </>
                          ) : (
                            <div>
                              Plan already exists on {duplicateCount} location(s). This action creates duplicate plans
                              for each location.
                            </div>
                          )}
                        </Tooltip.Content>
                      </Tooltip.Root>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            {useNewLocationsLabel
              ? `${selectedLocationIds.length} new ${pluralize("location", selectedLocationIds.length)}`
              : `Locations (${selectedLocationIds.length})`}
          </h4>
          <div className={styles.locationTreeWrapper}>
            <ReadOnlyTreeSelect
              className={styles.locationTree}
              tree={treeData}
              selectedIds={selectedLocationIds}
              flatDisplay={false}
              shouldEstablishParent={(node) => (node.descriptor as LocationType | undefined) === topLocationLevel}
              getLabelContext={(node, parentName) => {
                const isTopLevel = (node.descriptor as LocationType | undefined) === topLocationLevel;
                const displayLabel = !isTopLevel && parentName ? `${node.label}, ${parentName}` : node.label;
                const nameToPass = isTopLevel ? node.label : parentName;
                return { displayLabel, nameToPass };
              }}
              getDescriptorLabel={(descriptor) =>
                descriptor ? getLocationTypeLabel(descriptor as LocationType, builder?.locationSettings) : ""
              }
            />
          </div>
        </section>
      </div>
    </Tooltip.Provider>
  );
}
